/**
 * Logger Utility
 * 
 * Production-safe logging utility that:
 * - Only logs to console in development mode
 * - Buffers messages in development for debug panel display
 * - Provides structured logging with levels (debug, info, warn, error)
 * - Supports metadata and timestamp formatting
 * - Can be extended to send errors to error tracking services in production
 * 
 * Usage:
 *   import logger from '@/utils/logger';
 *   logger.debug('Debug message', { metadata });
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', { error: err });
 */

/* eslint-disable no-console */
// This file is the logger utility itself, so console statements are intentional

// Detect environment - Vite provides import.meta.env.DEV
// In production builds, import.meta.env.DEV is false
// In development, import.meta.env.DEV is true
const isDev = import.meta.env?.DEV ?? import.meta.env?.MODE === 'development';

const buffer = [];
const MAX_BUFFER_SIZE = 200;

/**
 * Safely convert a value to a string, handling edge cases like null-prototype objects
 */
const safeStringify = (value) => {
  if (typeof value === 'string') {return value;}
  if (value === null) {return 'null';}
  if (value === undefined) {return 'undefined';}

  try {
    // Try standard string conversion first
    if (typeof value === 'object') {
      // For objects, use JSON.stringify as it's safer for null-prototype objects
      return JSON.stringify(value);
    }
    return String(value);
  } catch (error) {
    // Fallback for objects that can't be stringified
    try {
      return Object.prototype.toString.call(value);
    } catch {
      return '[Unstringifiable Value]';
    }
  }
};

/**
 * Format log entry with timestamp and metadata
 */
const format = (level, message, meta) => ({
  timestamp: new Date().toISOString(),
  level,
  message: safeStringify(message),
  meta: meta || null,
});

/**
 * Send error to error tracking service (e.g., Sentry) in production
 * Uses optional error tracking integration from errorTracking.js
 */
const sendToErrorTracking = (level, message, meta) => {
  // Dynamically import error tracking to avoid bundling if not needed
  import('@/shared/utils/errorTracking.js')
    .then(({ captureException, captureMessage }) => {
      if (level === 'error') {
        // For errors, create an Error object and capture it
        const error = meta?.error instanceof Error
          ? meta.error
          : new Error(typeof message === 'string' ? message : String(message));

        captureException(error, {
          level: 'error',
          extra: {
            message: typeof message === 'string' ? message : String(message),
            meta,
          },
        });
      } else if (level === 'warn') {
        // For warnings, capture as message
        captureMessage(typeof message === 'string' ? message : String(message), 'warning', {
          extra: meta,
        });
      }
    })
    .catch(() => {
      // Error tracking not available - silently fail
      // This is expected if @sentry/react is not installed or DSN not configured
    });
};

const logger = {
  /**
   * Debug level logging - only in development
   */
  debug: (message, meta) => {
    const entry = format('debug', message, meta);
    if (isDev) {
      buffer.push(entry);
      if (buffer.length > MAX_BUFFER_SIZE) {
        buffer.shift(); // Remove oldest entry
      }
      if (meta !== undefined && meta !== null) {
        console.debug('[DEBUG]', message, meta);
      } else {
        console.debug('[DEBUG]', message);
      }
    }
  },

  /**
   * Info level logging - only in development
   */
  info: (message, meta) => {
    const entry = format('info', message, meta);
    if (isDev) {
      buffer.push(entry);
      if (buffer.length > MAX_BUFFER_SIZE) {
        buffer.shift();
      }
      if (meta !== undefined && meta !== null) {
        console.info('[INFO]', message, meta);
      } else {
        console.info('[INFO]', message);
      }
    }
  },

  /**
   * Warning level logging - always logged (but only to console in dev)
   */
  warn: (message, meta) => {
    const entry = format('warn', message, meta);
    if (isDev) {
      buffer.push(entry);
      if (buffer.length > MAX_BUFFER_SIZE) {
        buffer.shift();
      }
      if (meta !== undefined && meta !== null) {
        console.warn('[WARN]', message, meta);
      } else {
        console.warn('[WARN]', message);
      }
    }
    // In production, warnings could be sent to monitoring service
    sendToErrorTracking('warn', message, meta);
  },

  /**
   * Error level logging - always logged (but only to console in dev)
   * In production, errors should be sent to error tracking service
   */
  error: (message, meta) => {
    const entry = format('error', message, meta);
    if (isDev) {
      buffer.push(entry);
      if (buffer.length > MAX_BUFFER_SIZE) {
        buffer.shift();
      }
      if (meta !== undefined && meta !== null) {
        console.error('[ERROR]', message, meta);
      } else {
        console.error('[ERROR]', message);
      }
    }
    // Always send errors to tracking service in production
    sendToErrorTracking('error', message, meta);
  },

  /**
   * Get recent log entries for debug panel
   */
  getBuffer: () => buffer.slice(-MAX_BUFFER_SIZE),

  /**
   * Clear log buffer
   */
  clear: () => {
    buffer.length = 0;
  },

  /**
   * Get current buffer size
   */
  getBufferSize: () => buffer.length,
};

export default logger;
export { logger };
